import { useImperativeHandle, forwardRef, useState, useRef } from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { Buffer } from "buffer";
import { LogoDataUri } from "../../constants/logo";
import { pdf } from "@react-pdf/renderer";

global.Buffer = global.Buffer || Buffer;

// Estilos del PDF
const styles = StyleSheet.create({
  page: { backgroundColor: "#f7f7f7", padding: 20 },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    padding: 10,
    borderRadius: 5,
    minHeight: 80,
  },
  logo: {
    width: 150,
    height: "auto",
    objectFit: "contain",
  },
  headerContainerRight: {
    flex: 1,
    backgroundColor: "#223D51",
    borderTopLeftRadius: 50,
    borderBottomLeftRadius: 50,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-around",
    paddingHorizontal: 20,
    paddingVertical: 10,
    marginLeft: 50,
  },
  headerTextLarge: {
    fontFamily: "Helvetica",
    fontWeight: "500",
    fontSize: 10,
    color: "#FFFFFF",
    textAlign: "center",
    marginRight: 20,
  },
  headerTextSmall: {
    fontFamily: "Helvetica",
    fontWeight: "500",
    fontSize: 10,
    color: "#FFFFFF",
    textAlign: "center",
  },
  table: {
    display: "table",
    width: "100%",
    marginTop: 10,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 1,
  },
  tableColHeader: {
    width: "12.5%",
    backgroundColor: "#1D3557",
    color: "#FFFFFF",
    padding: 5,
    fontWeight: "bold",
    textAlign: "center",
    borderRightWidth: 2,
    borderRightColor: "#EEEEEE",
  },
  tableCol: {
    width: "12.5%",
    padding: 5,
    justifyContent: "center",
    alignItems: "center",
    borderRightWidth: 2,
    borderRightColor: "#EEEEEE",
  },
  tableCell: {
    fontFamily: "Helvetica",
    fontWeight: "700",
    fontSize: 8,
    textAlign: "center",
    color: "#223D51",
  },
  tableHeader: {
    fontFamily: "Helvetica",
    fontWeight: "700",
    fontSize: 10,
    textAlign: "center",
    color: "#FFFFFF",
  },
});

// Alternar colores de filas
const getRowStyle = (index) => ({
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#DDDDDD",
});

// Función para formatear la fecha
const formatDate = (dateString) => {
  if (!dateString) return "Sin definir";
  const date = new Date(dateString);
  return isNaN(date)
    ? "Sin definir"
    : `${String(date.getDate()).padStart(2, "0")}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}-${date.getFullYear()}`;
};

const statusColors = {
  VIGENTE: "green",
  EXPIRADO: "red",
  PROXIMO_A_EXPIRAR: "orange",
  "Sin definir": "gray",
};

const calculateContractStatus = (fechaVencimiento) => {
  if (!fechaVencimiento) return "Sin definir";

  const fechaVenc = new Date(fechaVencimiento);
  const fechaActual = new Date();
  const diferenciaDias = Math.floor(
    (fechaVenc - fechaActual) / (1000 * 60 * 60 * 24)
  );

  if (diferenciaDias < 0) return "EXPIRADO";
  if (diferenciaDias <= 60) return "PROXIMO_A_EXPIRAR";
  return "VIGENTE";
};

const safeText = (value) =>
  typeof value === "string"
    ? value
    : value && typeof value === "object"
    ? "[Objeto]"
    : "Sin definir";

// Función para formatear nombres y empresas correctamente
const capitalizeText = (text) => {
  if (!text) return "Sin definir";

  // Excepciones que deben permanecer en minúsculas
  const exceptions = ["de", "del", "y"];

  // Separar en palabras y capitalizar correctamente
  let formattedText = text
    .toLowerCase()
    .split(" ")
    .map((word) =>
      exceptions.includes(word)
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(" ");

  // Asegurar que "S.A. de C.V." siempre esté bien escrito
  formattedText = formattedText
    .replace(/\b(s\.?a\.?|sa)\b/gi, "S.A.") // Asegura "S.A."
    .replace(/\b(c\.?v\.?|cv)\b/gi, "C.V.") // Asegura "C.V."
    .replace(/S\.A\.\sde\sC\.V(?!\.)/gi, "S.A. de C.V."); // Asegura que termine en "S.A. de C.V."

  return formattedText;
};

// Componente PDF principal
const DocumentPDF = ({ data }) => {
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <Document>
        <Page size="LETTER" style={styles.page}>
          <Text style={styles.title}>No hay datos disponibles para el PDF</Text>
        </Page>
      </Document>
    );
  }

  const today = new Date();
  const todayFormatted = `${String(today.getDate()).padStart(2, "0")}-${String(
    today.getMonth() + 1
  ).padStart(2, "0")}-${today.getFullYear()}`;

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.header}>
          {/* Logo a la izquierda */}
          <Image src={LogoDataUri} style={styles.logo} />

          {/* Contenedor a la derecha */}
          <View style={styles.headerContainerRight}>
            <Text style={styles.headerTextLarge}>
              Reporte de Documentos de Instaladores
            </Text>
            <Text style={styles.headerTextSmall}>{todayFormatted}</Text>
          </View>
        </View>

        <View style={styles.table}>
          {/* Encabezados de la tabla */}
          <View style={styles.tableRow}>
            {[
              "Nombre",
              "Gafete",
              "Empresa",
              "Contrato",
              "INE",
              "IMSS",
              "Cert. Médico",
              "DC3",
            ].map((header, index) => (
              <View key={index} style={styles.tableColHeader} wrap={false}>
                <Text style={styles.tableHeader}>{header}</Text>
              </View>
            ))}
          </View>

          {/* Filas de la tabla */}
          {data.map((row, index) => (
            <View key={index} style={[styles.tableRow, getRowStyle(index)]}>
              <View style={styles.tableCol} wrap={false}>
                <Text style={styles.tableCell}>
                  {capitalizeText(
                    `${safeText(row.nombre)} ${safeText(row.apellidos)}`
                  )}
                </Text>
              </View>
              <View style={styles.tableCol} wrap={false}>
                <Text style={styles.tableCell}>{safeText(row.numGafete)}</Text>
              </View>
              <View style={styles.tableCol} wrap={false}>
                <Text style={styles.tableCell}>
                  {capitalizeText(safeText(row.empresa?.razonSocial))}
                </Text>
              </View>
              <View style={styles.tableCol} wrap={false}>
                <View
                  style={{
                    border: `1px solid ${
                      statusColors[
                        calculateContractStatus(
                          row.empresa?.contrato?.fechaVencimiento
                        )
                      ] || "gray"
                    }`,
                    borderRadius: 14,
                    paddingVertical: 2,
                    paddingHorizontal: 2,
                    marginHorizontal: 0.5,
                    alignItems: "center",
                    justifyContent: "center",
                    alignContent: "center",
                    verticalAlign: "middle",
                    minWidth: 60,
                    maxWidth: "90%",
                  }}
                >
                  <Text style={[styles.tableCell, { padding: 0 }]}>
                    {formatDate(row.empresa?.contrato?.fechaVencimiento)}
                  </Text>
                </View>
              </View>

              <View style={styles.tableCol} wrap={false}>
                <View
                  style={{
                    border: `1px solid ${
                      statusColors[safeText(row.vigenciaIne?.name)] || "gray"
                    }`,
                    borderRadius: 14,
                    paddingVertical: 2,
                    paddingHorizontal: 2,
                    marginHorizontal: 0.5,
                    alignItems: "center",
                    justifyContent: "center",
                    alignContent: "center",
                    verticalAlign: "middle",
                    minWidth: 60,
                    maxWidth: "90%",
                  }}
                >
                  <Text style={[styles.tableCell, { padding: 0 }]}>
                    {formatDate(row.fechaVencimientoIne)}
                  </Text>
                </View>
              </View>

              <View style={styles.tableCol} wrap={false}>
                <View
                  style={{
                    border: `1px solid ${
                      statusColors[safeText(row.vigenciaImss?.name)] || "gray"
                    }`,
                    borderRadius: 14,
                    paddingVertical: 2,
                    paddingHorizontal: 2,
                    marginHorizontal: 0.5,
                    alignItems: "center",
                    justifyContent: "center",
                    alignContent: "center",
                    verticalAlign: "middle",
                    minWidth: 60,
                    maxWidth: "90%",
                  }}
                >
                  <Text style={[styles.tableCell, { padding: 0 }]}>
                    {formatDate(row.fechaVencimientoImss)}
                  </Text>
                </View>
              </View>

              <View style={styles.tableCol} wrap={false}>
                <View
                  style={{
                    border: `1px solid ${
                      statusColors[
                        safeText(row.vigenciaCertificadoMedico?.name)
                      ] || "gray"
                    }`,
                    borderRadius: 14,
                    paddingVertical: 2,
                    paddingHorizontal: 2,
                    marginHorizontal: 0.5,
                    alignItems: "center",
                    justifyContent: "center",
                    alignContent: "center",
                    verticalAlign: "middle",
                    minWidth: 60,
                    maxWidth: "90%",
                  }}
                >
                  <Text style={[styles.tableCell, { padding: 0 }]}>
                    {formatDate(row.fechaVencimientoCertificadoMedico)}
                  </Text>
                </View>
              </View>

              <View style={styles.tableCol} wrap={false}>
                <View
                  style={{
                    border: `1px solid ${
                      statusColors[safeText(row.vigenciaDc3?.name)] || "gray"
                    }`,
                    borderRadius: 14,
                    paddingVertical: 2,
                    paddingHorizontal: 2,
                    marginHorizontal: 0.5,
                    alignItems: "center",
                    justifyContent: "center",
                    alignContent: "center",
                    verticalAlign: "middle",
                    minWidth: 60,
                    maxWidth: "90%",
                  }}
                >
                  <Text style={[styles.tableCell, { padding: 0 }]}>
                    {formatDate(row.fechaVencimientoDc3)}
                  </Text>
                </View>
              </View>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

// Componente de descarga de PDF
const PDFInstallers = forwardRef(({ data }, ref) => {
  const [downloadUrl, setDownloadUrl] = useState(null);
  const downloadRef = useRef(null);

  useImperativeHandle(ref, () => ({
    generarPDF: () => {
      const blobStream = pdf(<DocumentPDF data={data} />).toBlob();
      blobStream.then((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "documentos_instaladores.pdf";
        link.click();
        URL.revokeObjectURL(url);
      });
    },
  }));

  return null; // Ya no se muestra botón aquí
});

export default PDFInstallers;
